import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';

// Sous-hook : actions sur les emails (sync, sélection, important, spam, suppression, actions en masse) — iso-comportement
export function useMailActions({
  backendUrl, authToken, selectedAccount,
  emails, setEmails, selectedEmail, setSelectedEmail, setUnreadCount,
  currentFolder, loadEmails, showSuccess, showError,
  onNavigateToClient, onNavigateToCourtier, onNavigateToCompany, onNavigateToAssisteur, onNavigateToAssistanceClient
}) {
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [trackingStats, setTrackingStats] = useState(null);

  // ========== Synchronisation automatique des emails ==========
  const syncEmailsSilent = React.useCallback(async () => {
    if (!selectedAccount || syncing) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ account_id: selectedAccount.id })
      });

      if (response.ok) {
        const result = await response.json();
        const count = result.synced_count || 0;
        if (count > 0) {
          await loadEmails();
          console.log(`[MailHub] Auto-sync: ${count} nouveaux emails`);
        }
      }
    } catch (error) {
      console.error('[MailHub] Auto-sync error:', error);
    }
  }, [selectedAccount, syncing, backendUrl, authToken, loadEmails]);

  // Lancer la synchronisation automatique toutes les 60 secondes
  useEffect(() => {
    if (!selectedAccount) return;

    syncEmailsSilent();

    const syncInterval = setInterval(() => {
      syncEmailsSilent();
    }, 60000);

    return () => clearInterval(syncInterval);
  }, [selectedAccount, syncEmailsSilent]);
  // ========== Fin synchronisation automatique ==========

  const syncEmails = async () => {
    if (!selectedAccount) return;

    setSyncing(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ account_id: selectedAccount.id })
      });

      if (response.ok) {
        const result = await response.json();
        const count = result.synced_count || 0;
        showSuccess(`${count} nouvel${count > 1 ? 'x' : ''} email${count > 1 ? 's' : ''} synchronisé${count > 1 ? 's' : ''}`);
        await loadEmails();
      } else {
        throw new Error('Erreur de synchronisation');
      }
    } catch (error) {
      logger.error('Error syncing emails:', error);
      showError('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const loadTrackingStats = async (emailId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/tracking-stats/${emailId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const stats = await response.json();
        setTrackingStats(stats);
      }
    } catch (error) {
      logger.error('Error loading tracking stats:', error);
    }
  };

  const selectEmail = async (email) => {
    setSelectedEmail(email);

    if (email.direction === 'outbound') {
      loadTrackingStats(email.id);
    } else {
      setTrackingStats(null);
    }

    if (!email.is_read) {
      try {
        await fetch(`${backendUrl}/api/admin/mail/inbox/${email.id}/mark-read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_read: true })
        });

        setEmails(emails.map(e =>
          e.id === email.id ? { ...e, is_read: true } : e
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        logger.error('Error marking email as read:', error);
      }
    }
  };

  const navigateEmail = (direction) => {
    if (!selectedEmail || !emails.length) return;

    const currentIndex = emails.findIndex(e => e.id === selectedEmail.id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < emails.length) {
      selectEmail(emails[newIndex]);
    }
  };

  const hasPreviousEmail = () => {
    if (!selectedEmail || !emails.length) return false;
    const currentIndex = emails.findIndex(e => e.id === selectedEmail.id);
    return currentIndex > 0;
  };

  const hasNextEmail = () => {
    if (!selectedEmail || !emails.length) return false;
    const currentIndex = emails.findIndex(e => e.id === selectedEmail.id);
    return currentIndex < emails.length - 1;
  };

  const toggleImportant = async (emailId, currentStatus) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/inbox/${emailId}/mark-important`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_important: !currentStatus })
      });

      if (response.ok) {
        setEmails(emails.map(e =>
          e.id === emailId ? { ...e, is_important: !currentStatus } : e
        ));

        if (selectedEmail?.id === emailId) {
          setSelectedEmail({ ...selectedEmail, is_important: !currentStatus });
        }

        showSuccess(!currentStatus ? 'Email marqué comme important' : 'Email retiré des importants');
      }
    } catch (error) {
      logger.error('Error toggling important:', error);
      showError('Erreur lors de la mise à jour');
    }
  };

  const processEmail = async (emailId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/email/${emailId}/process`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        showSuccess('Email marqué comme traité');
        setEmails(emails.filter(e => e.id !== emailId));
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }
      }
    } catch (error) {
      logger.error('Error processing email:', error);
      showError('Erreur lors du traitement');
    }
  };

  const processBulkEmails = async (days = 7) => {
    if (!selectedAccount) {
      showError('Veuillez sélectionner un compte mail');
      return;
    }

    if (!window.confirm(`Voulez-vous classer tous les emails liés à une fiche CRM (avec badge) du compte "${selectedAccount.email_address}" ?\n\nCes emails seront retirés de la boîte de réception et resteront visibles sur les fiches CRM correspondantes.`)) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/process-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: selectedAccount.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(`${data.count} emails classés (visibles sur fiches CRM)`);
        await loadEmails();
      } else {
        const errorData = await response.json();
        showError(errorData.detail || 'Erreur lors du traitement en masse');
      }
    } catch (error) {
      logger.error('Error bulk processing emails:', error);
      showError('Erreur lors du traitement en masse');
    }
  };

  const deleteEmail = async (emailId, isPermanent = false) => {
    const confirmMessage = isPermanent
      ? 'Êtes-vous sûr de vouloir supprimer définitivement cet email ?'
      : 'Déplacer cet email vers la corbeille ?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const url = `${backendUrl}/api/admin/mail/email/${emailId}${isPermanent ? '?permanent=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        showSuccess(isPermanent ? 'Email supprimé définitivement' : 'Email déplacé vers la corbeille');

        setEmails(emails.filter(e => e.id !== emailId));

        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }

        await loadEmails();
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      logger.error('Error deleting email:', error);
      showError('Erreur lors de la suppression de l\'email');
    }
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev =>
      prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEmailIds.length === emails.length) {
      setSelectedEmailIds([]);
    } else {
      setSelectedEmailIds(emails.map(e => e.id));
    }
  };

  const bulkDelete = async () => {
    if (selectedEmailIds.length === 0) return;

    const isPermanent = currentFolder === 'trash';
    const confirmMessage = isPermanent
      ? `Supprimer définitivement ${selectedEmailIds.length} email(s) ?`
      : `Déplacer ${selectedEmailIds.length} email(s) vers la corbeille ?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const promises = selectedEmailIds.map(emailId =>
        fetch(`${backendUrl}/api/admin/mail/email/${emailId}${isPermanent ? '?permanent=true' : ''}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      );

      await Promise.all(promises);

      showSuccess(`${selectedEmailIds.length} email(s) ${isPermanent ? 'supprimé(s) définitivement' : 'déplacé(s) vers la corbeille'}`);
      setSelectedEmailIds([]);
      await loadEmails();
    } catch (error) {
      logger.error('Error bulk deleting:', error);
      showError('Erreur lors de la suppression groupée');
    }
  };

  const markAsSpam = async (emailId) => {
    if (!window.confirm('Marquer cet email comme spam ? Tous les futurs emails de cet expéditeur seront automatiquement classés en spam.')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/email/${emailId}/mark-spam`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ create_rule: true })
      });

      if (response.ok) {
        showSuccess('Email marqué comme spam et règle créée');

        setEmails(emails.filter(e => e.id !== emailId));

        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }

        await loadEmails();
      } else {
        throw new Error('Erreur lors du marquage spam');
      }
    } catch (error) {
      logger.error('Error marking as spam:', error);
      showError('Erreur lors du marquage comme spam');
    }
  };

  const unmarkSpam = async (emailId) => {
    if (!window.confirm('Retirer cet email du spam ? La règle de blocage pour cet expéditeur sera supprimée.')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/email/${emailId}/unmark-spam`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        showSuccess('Email retiré du spam et règle supprimée');

        setEmails(emails.filter(e => e.id !== emailId));

        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }

        await loadEmails();
      } else {
        throw new Error('Erreur lors du retrait du spam');
      }
    } catch (error) {
      logger.error('Error unmarking spam:', error);
      showError('Erreur lors du retrait du spam');
    }
  };

  const bulkUnmarkSpam = async () => {
    if (selectedEmailIds.length === 0) return;

    if (!window.confirm(`Retirer ${selectedEmailIds.length} email(s) du spam ?`)) {
      return;
    }

    try {
      const promises = selectedEmailIds.map(emailId =>
        fetch(`${backendUrl}/api/admin/mail/email/${emailId}/unmark-spam`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        })
      );

      await Promise.all(promises);

      showSuccess(`${selectedEmailIds.length} email(s) retiré(s) du spam`);
      setSelectedEmailIds([]);
      await loadEmails();
    } catch (error) {
      logger.error('Error bulk unmarking spam:', error);
      showError('Erreur lors du retrait du spam groupé');
    }
  };

  const bulkMarkAsUnread = async () => {
    if (selectedEmailIds.length === 0) return;

    try {
      const promises = selectedEmailIds.map(emailId =>
        fetch(`${backendUrl}/api/admin/mail/inbox/${emailId}/mark-read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_read: false })
        })
      );

      await Promise.all(promises);

      showSuccess(`${selectedEmailIds.length} email(s) marqué(s) comme non lu(s)`);
      setSelectedEmailIds([]);
      await loadEmails();
    } catch (error) {
      logger.error('Error bulk marking as unread:', error);
      showError('Erreur lors du marquage groupé');
    }
  };

  const bulkMarkAsRead = async () => {
    if (selectedEmailIds.length === 0) return;

    try {
      const promises = selectedEmailIds.map(emailId =>
        fetch(`${backendUrl}/api/admin/mail/inbox/${emailId}/mark-read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_read: true })
        })
      );

      await Promise.all(promises);

      showSuccess(`${selectedEmailIds.length} email(s) marqué(s) comme lu(s)`);
      setSelectedEmailIds([]);
      await loadEmails();
    } catch (error) {
      logger.error('Error bulk marking as read:', error);
      showError('Erreur lors du marquage groupé');
    }
  };

  const bulkMarkAsSpam = async () => {
    if (selectedEmailIds.length === 0) return;

    if (!window.confirm(`Marquer ${selectedEmailIds.length} email(s) comme spam ?`)) {
      return;
    }

    try {
      const promises = selectedEmailIds.map(emailId =>
        fetch(`${backendUrl}/api/admin/mail/email/${emailId}/mark-spam`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ create_rule: false })
        })
      );

      await Promise.all(promises);

      showSuccess(`${selectedEmailIds.length} email(s) marqué(s) comme spam`);
      setSelectedEmailIds([]);
      await loadEmails();
    } catch (error) {
      logger.error('Error bulk marking as spam:', error);
      showError('Erreur lors du marquage spam groupé');
    }
  };

  const handleCRMLink = (email) => {
    if (email.linked_client_id && onNavigateToClient) {
      onNavigateToClient(email.linked_client_id);
    } else if (email.linked_courtier_id && onNavigateToCourtier) {
      onNavigateToCourtier(email.linked_courtier_id);
    } else if (email.linked_company_id && onNavigateToCompany) {
      onNavigateToCompany(email.linked_company_id);
    } else if (email.linked_assisteur_id && onNavigateToAssisteur) {
      onNavigateToAssisteur(email.linked_assisteur_id);
    } else if (email.linked_assistance_client_id && onNavigateToAssistanceClient) {
      onNavigateToAssistanceClient(email.linked_assistance_client_id);
    }
  };

  // ── Vider un dossier entier (corbeille ou spam) ─────────────────────────────
  const emptyFolder = React.useCallback(async (folder) => {
    if (!selectedAccount) return;
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/folder/${folder}/empty?account_id=${selectedAccount.id}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (!response.ok) throw new Error('Erreur serveur');
      const data = await response.json();
      const label = folder === 'trash' ? 'corbeille' : 'spam';
      showSuccess(`${data.deleted} email${data.deleted > 1 ? 's' : ''} supprimé${data.deleted > 1 ? 's' : ''} de la ${label}`);
      await loadEmails(folder);
    } catch (err) {
      showError('Erreur lors du vidage du dossier');
      logger.error('emptyFolder error:', err);
    }
  }, [backendUrl, authToken, selectedAccount, loadEmails, showSuccess, showError]);

  return {
    selectedEmailIds, setSelectedEmailIds,
    syncing, trackingStats,
    syncEmails, selectEmail, navigateEmail, hasPreviousEmail, hasNextEmail, loadTrackingStats,
    toggleImportant, processEmail, processBulkEmails, deleteEmail,
    toggleEmailSelection, toggleSelectAll, bulkDelete, emptyFolder,
    markAsSpam, unmarkSpam, bulkUnmarkSpam, bulkMarkAsUnread, bulkMarkAsRead, bulkMarkAsSpam,
    handleCRMLink
  };
}
